import { forwardRef, useId } from 'react';

import { TextInput, type TextInputProps } from './TextInput.js';

export type FormFieldProps = Omit<
  TextInputProps,
  'id' | 'aria-describedby' | 'aria-invalid' | 'aria-labelledby'
> & {
  /** Visible label rendered with a programmatic association to the input. */
  label: string;
  /** Validation message; sets aria-invalid and links via aria-describedby. Use for field-level validation — prefer `tone="danger"` on Alert/Snackbar for page-level feedback. */
  error?: string;
  /** Supplementary hint text below the input. */
  help?: string;
  id?: string;
};

/** Label + input + help/error with accessible associations for form controls. */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, error, help, id: idProp, className, ...inputProps },
  ref,
): React.JSX.Element {
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const helpId = help ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="form-field">
      <label htmlFor={inputId} className="form-field__label">
        {label}
      </label>
      <TextInput
        ref={ref}
        id={inputId}
        className={className}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        {...inputProps}
      />
      {help ? (
        <p id={helpId} className="form-field__help">
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="form-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
